import { graphyte } from '@comeon/analytics';
import queryString from 'query-string';
import type { Location } from 'react-router-dom';
import { selectLocale } from '~/components/account/ducks/index';
import {
	selectCasinoEndHeartbeatUrl,
	selectCasinoHeartbeatInterval,
	selectCasinoHeartbeatUrl,
	selectIsDesktop,
	selectMobileIngameToolbar,
	selectSiteHostname
} from '~/components/brand/ducks/selectors';
import { gamesByGameCodeSelector } from '~/components/casino-shared/all-games/ducks/selectors';
import { inhouseJackpotAvailableInCurrentGameSelector } from '~/components/casino-shared/inhouse-jackpot/ducks/index';
import providerLoader from '~/components/casino-shared/providers/provider-loader';
import getGameUrls from '~/components/casino-shared/utilities/getGameUrls';
import { previousLocationSelector } from '~/components/casino/ducks/selectors';
import { selectHasRegulationsHeader } from '~/components/header/regulations-header/index';
import { requestApi, type BaseResponse } from '~/services/api-new';
import type { GAME_MODE } from '../../constants';
import type { CasinoSharedAction, CasinoSharedThunkAction } from '../../ducks/types';
import type { EnhancedGame, Game } from '../../types';
import {
	CLEAN_UP,
	SET_CLEANUP_LISTENERS,
	SET_CURRENT_GAME,
	SET_GAME_LOADER_REQUEST_PENDING,
	SET_GAME_LOADER_RESPONSE,
	SET_GAME_MODE,
	SET_LOAD_AS
} from './constants';
import { startHeartbeat, stopHeartbeat } from './heartbeat';
import { registerReducer } from './reducer';
import {
	cleanUpListenersSelector,
	currentGameSelector,
	gameLoaderRequestPendingSelector,
	hydratedSelector
} from './selectors';
import type { GameLoaderData, GameLoaderResponse } from './types';
import { basePathname } from '~/utils/pathname';

registerReducer();

export const cleanUp = (): CasinoSharedThunkAction => (dispatch, getState) => {
	const state = getState();
	const cleanUpListeners = cleanUpListenersSelector(state);
	if (cleanUpListeners) {
		cleanUpListeners();
	}
	stopHeartbeat();
	dispatch({ type: CLEAN_UP });
};

const setCurrentGame = (
	result: Partial<EnhancedGame> & {
		realModeUrl: string;
		funModeUrl: string;
	}
): CasinoSharedAction => ({
	type: SET_CURRENT_GAME,
	payload: result
});

const setLoadAs = (loadAs: string): CasinoSharedAction => ({
	type: SET_LOAD_AS,
	payload: loadAs
});

export const setGameMode = (gameMode: (typeof GAME_MODE)[keyof typeof GAME_MODE]): CasinoSharedAction => ({
	type: SET_GAME_MODE,
	payload: gameMode
});

export const setCleanupListeners = (listeners: () => void): CasinoSharedAction => ({
	type: SET_CLEANUP_LISTENERS,
	payload: listeners
});

const setGameLoaderRequestPending = (isPending: boolean): CasinoSharedAction => ({
	type: SET_GAME_LOADER_REQUEST_PENDING,
	payload: isPending
});

export const fetchGameByGameCode =
	(gameCode: string): CasinoSharedThunkAction =>
	async (dispatch, getState, { license }) => {
		const gamesByGameCode = gamesByGameCodeSelector(getState());
		const game = gamesByGameCode ? gamesByGameCode[gameCode] : null;
		if (game) {
			return dispatch(
				setCurrentGame({
					...game,
					...getGameUrls(game, license)
				})
			);
		}
		try {
			const response = await requestApi<BaseResponse<[Game]>>({
				endpoint: `/rest/state/casino/games/v2/${gameCode}`
			});

			if (response.status === 'SUCCESS' && response.result.length) {
				const [fetchedGame] = response.result;
				if (fetchedGame) {
					return dispatch(
						setCurrentGame({
							...fetchedGame,
							...getGameUrls(fetchedGame, license),
							imageView: undefined
						})
					);
				}
			}
			/**
			 * When user open game on desktop by using mobile game code result is empty
			 * for ex. somebody shared a link from mobile, we need to load a desktop version anyway
			 */
			return dispatch(
				setCurrentGame({
					code: gameCode,
					...getGameUrls({ code: gameCode }, license)
				})
			);
		} catch (err) {
			/* eslint-disable no-console */
			console.error(err);
			return undefined;
		}
	};

const setGameLoaderResponse =
	(response: GameLoaderResponse): CasinoSharedThunkAction =>
	(dispatch, getState) => {
		const state = getState();
		const hearbeatUrl = selectCasinoHeartbeatUrl(state);
		const endHearbeatUrl = selectCasinoEndHeartbeatUrl(state);
		const interval = selectCasinoHeartbeatInterval(state);

		if (hearbeatUrl && response?.status === 'SUCCESS') {
			startHeartbeat(response?.result?.gameLoaderResponse?.gameSessionId, hearbeatUrl, endHearbeatUrl, interval);
		}

		dispatch({
			type: SET_GAME_LOADER_RESPONSE,
			payload: response
		});
	};

export const fetchGameLoader =
	(
		location: Location<{
			listName?: string;
			listGameIndex?: string;
			selectedSeat?: string;
		}>,
		gameMode: (typeof GAME_MODE)[keyof typeof GAME_MODE],
		gameCode: string
	): CasinoSharedThunkAction =>
	async (dispatch, getState) => {
		const state = getState();

		const gameLoaderRequestPending = gameLoaderRequestPendingSelector(state);
		const currentGame = currentGameSelector(state);
		const locale = selectLocale(state);
		const hydrated = hydratedSelector(state);
		const siteHostname = selectSiteHostname(state);

		const listName = location?.state?.listName;
		const listGameIndex = location?.state?.listGameIndex;
		const selectedSeat = location?.state?.selectedSeat ? `?seat=${location?.state?.selectedSeat}` : '';

		if (gameLoaderRequestPending || hydrated) return;

		dispatch(setGameLoaderRequestPending(true));

		try {
			const response = await requestApi<GameLoaderResponse>({
				endpoint: `/load/${encodeURIComponent(gameCode)}/${gameMode.toUpperCase()}/800/600${selectedSeat}`
			});

			dispatch(setGameLoaderResponse(response));

			if (response.status === 'SUCCESS' && currentGame) {
				const {
					gameLoaderResponse: { gameSessionId }
				} = response.result;

				graphyte.trackGamePlay({
					gameId: currentGame.gameId,
					name: currentGame.name,
					categoryCode: currentGame.categoryCode,
					gameSessionId,
					locale,
					siteHostname,
					listName,
					listGameIndex
				});
			}
		} catch (error) {
			/* eslint-disable no-console */
			console.error(error);
		}

		dispatch(setGameLoaderRequestPending(false));
	};

export const loadGame =
	(
		response: GameLoaderData['gameLoaderResponse'] | null,
		location: Location,
		gameMode: (typeof GAME_MODE)[keyof typeof GAME_MODE],
		gameLoaderTarget: Element,
		isQuickDepositAvailable: boolean
	): CasinoSharedThunkAction =>
	(dispatch, getState, { license }) => {
		const state = getState();

		const isMobileIngameToolbarEnabled = selectMobileIngameToolbar(state);
		const hasRegulationHeader = selectHasRegulationsHeader(state);
		const isDesktop = selectIsDesktop(state);
		const previousLocation = previousLocationSelector(state);
		const inhouseJackpotAvailableInCurrentGame = inhouseJackpotAvailableInCurrentGameSelector(state);

		switch (response?.gameLoadType) {
			case 'REDIRECTION':
				window.location.replace(response.data.URL);
				break;
			case 'SCRIPT':
				dispatch(setLoadAs('SCRIPT'));
				break;
			case 'JSON': {
				let jsonObject;

				try {
					jsonObject = JSON.parse(response.data.JSON) as {
						provider?: string;
						parameters: {
							URL: string;
						};
					};
				} catch (err) {
					return console.error('Error when parsing JSON response during game loading');
				}

				if (!jsonObject.provider) {
					jsonObject.provider = response.provider.toLowerCase();
				}

				const dataWithParams = {
					...jsonObject,
					parameters: {
						...jsonObject.parameters,
						extra: queryString.parse(location.search)
					}
				};

				const hasInGameMobileToolbar =
					!isDesktop &&
					isMobileIngameToolbarEnabled &&
					(inhouseJackpotAvailableInCurrentGame || isQuickDepositAvailable);

				// eslint-disable-next-line @typescript-eslint/no-unsafe-call
				providerLoader
					.load(
						dataWithParams,
						response.errorMessage,
						gameMode,
						gameLoaderTarget,
						{
							regulation: response.regulation,
							hasRegulationHeader,
							hasInGameMobileToolbar,
							isDesktop
						},
						previousLocation,
						basePathname(license)
					)
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
					.then((cleanUpListeners: () => void) => {
						dispatch(setCleanupListeners(cleanUpListeners));
					});

				dispatch(setLoadAs('JSON'));

				break;
			}
			default:
				dispatch(setLoadAs('IFRAME'));
		}

		dispatch(setGameMode(gameMode));
	};
